// Native Singular marketing layer — consent-gated, mirrors analytics.ts.
// All calls no-op on web; Singular runs via app-local Capacitor plugins (iOS + Android).

import { Capacitor } from '@capacitor/core';
import { initializeSingular, isSingularConfigured, getSingular } from './singularInit';
import { requiresConsent, getStoredConsent } from './region';
import type { EventName } from './analytics-events';

let pendingUserId: string | null = null;

function consentGranted(): boolean {
  return !(requiresConsent() && getStoredConsent() !== 'granted');
}

function active(): boolean {
  return Capacitor.isNativePlatform() && consentGranted() && isSingularConfigured();
}

// Whitelist of Mixpanel events forwarded to Singular (name only — no props).
// Typed against EventName so a renamed event fails the build instead of silently dropping.
const SINGULAR_EVENT_MAP: Partial<Record<EventName, string>> = {
  'Email Verified': 'sng_complete_registration',
  'Onboarding Completed': 'onboarding_completed',
  'Vent Posted': 'vent_posted',
  'Message Sent': 'message_sent',
  'Room Joined': 'room_joined',
};

// Native plugin calls are async; we fire-and-forget but swallow rejections so a
// bridge error never surfaces as an unhandled promise.
function fire(p: Promise<void> | undefined): void {
  p?.catch(() => {});
}

/** Consent-gated init. Driven from the same path as Mixpanel's initIfReady(). */
export async function maybeInitSingular(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (!consentGranted()) return;
  const ok = await initializeSingular(pendingUserId ?? undefined);
  // A login that landed while init was in flight still gets applied.
  if (ok && pendingUserId) fire(getSingular()?.setCustomUserId({ userId: pendingUserId }));
}

export function setSingularUserId(userId: string): void {
  pendingUserId = userId;
  if (isSingularConfigured()) fire(getSingular()?.setCustomUserId({ userId }));
}

export function unsetSingularUserId(): void {
  pendingUserId = null;
  if (isSingularConfigured()) fire(getSingular()?.unsetCustomUserId());
}

/** Consent withdrawn — stop sending data without tearing down the SDK. */
export function stopSingular(): void {
  if (isSingularConfigured()) fire(getSingular()?.stopAllTracking());
}

/** Consent re-granted / opt back in. */
export function resumeSingular(): void {
  if (isSingularConfigured()) fire(getSingular()?.resumeAllTracking());
}

export function trackSingularEvent(name: string, args?: Record<string, unknown>): void {
  if (!active()) return;
  const p = getSingular();
  if (!p) return;
  if (args) fire(p.eventWithArgs({ name, args }));
  else fire(p.event({ name }));
}

// Revenue is wired and ready but unused — RevenueCat purchases aren't live yet.
// When subscriptions go live, call this on a confirmed purchase, e.g. in
// useRevenueCat.purchasePackage() success:
//   trackSingularRevenue(pkg.product.currencyCode, pkg.product.price);
export function trackSingularRevenue(
  currency: string,
  amount: number,
  args?: Record<string, unknown>,
): void {
  if (!active()) return;
  const p = getSingular();
  if (!p) return;
  fire(p.revenue({ currency, amount, args }));
}

/** Forwards a whitelisted analytics event to Singular. Called from analytics.track(). */
export function forwardToSingular(eventName: string): void {
  const mapped = SINGULAR_EVENT_MAP[eventName as EventName];
  if (mapped) trackSingularEvent(mapped);
}
